const mathjs = require("mathjs");

export function cdfNormal(x, mean = 0, std = 1) {
  return (1 - mathjs.erf((mean - x) / (Math.sqrt(2) * std))) / 2;
}

export function z_smile(time, fwd, strike, d0) {
  return cdfNormal(Math.log(fwd / strike) / (d0 * Math.sqrt(time)), 0, 1);
}

export function qVolatility(data, strike) {
  let z = z_smile(data.days / 365, data.value, strike, data.d0);
  return Math.exp(data.c0 + data.c1 * z + data.c2 * z ** 2);
}

export function NormSInv(p) {
  var a1 = -39.6968302866538,
    a2 = 220.946098424521,
    a3 = -275.928510446969;
  var a4 = 138.357751867269,
    a5 = -30.6647980661472,
    a6 = 2.50662827745924;
  var b1 = -54.4760987982241,
    b2 = 161.585836858041,
    b3 = -155.698979859887;
  var b4 = 66.8013118877197,
    b5 = -13.2806815528857,
    c1 = -7.78489400243029e-3;
  var c2 = -0.322396458041136,
    c3 = -2.40075827716184,
    c4 = -2.54973253934373;
  var c5 = 4.37466414146497,
    c6 = 2.93816398269878,
    d1 = 7.78469570904146e-3;
  var d2 = 0.32246712907004,
    d3 = 2.445134137143,
    d4 = 3.75440866190742;
  var p_low = 0.02425,
    p_high = 1 - p_low;
  var q, r;
  var retVal;

  if (p < 0 || p > 1) {
    alert("NormSInv: Argument out of range.");
    retVal = 0;
  } else if (p < p_low) {
    q = Math.sqrt(-2 * Math.log(p));
    retVal =
      (((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
      ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  } else if (p <= p_high) {
    q = p - 0.5;
    r = q * q;
    retVal =
      ((((((a1 * r + a2) * r + a3) * r + a4) * r + a5) * r + a6) * q) /
      (((((b1 * r + b2) * r + b3) * r + b4) * r + b5) * r + 1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    retVal =
      -(((((c1 * q + c2) * q + c3) * q + c4) * q + c5) * q + c6) /
      ((((d1 * q + d2) * q + d3) * q + d4) * q + 1);
  }

  return retVal;
}

export function callDeltaToStrike(spot, time, r, rf, volatility, delta) {
  return (
    spot *
    Math.exp(
      -NormSInv(delta * Math.exp(rf * time)) * volatility * Math.sqrt(time) +
        (r - rf + 0.5 * volatility ** 2) * time
    )
  );
}

export function putDeltaToStrike(spot, time, r, rf, volatility, delta) {
  delta = -Math.abs(delta);
  return (
    spot *
    Math.exp(
      NormSInv(-delta * Math.exp(rf * time)) * volatility * Math.sqrt(time) +
        (r - rf + 0.5 * volatility ** 2) * time
    )
  );
}

export function strikeToDelta(d, strike, spot, v, callOrPut) {
  let omega,
    time = d.days / 365,
    r = d.r,
    rf = d.rf;
  let d1 =
    (Math.log(spot / strike) + (r - rf + v ** 2 / 2) * time) /
    (v * Math.sqrt(time));
  if (callOrPut === 1) {
    // call
    omega = 1;
  } else {
    // put
    omega = -1;
  }
  return Math.abs(omega * Math.exp(-rf * time) * cdfNormal(omega * d1));
}

export function d1d2BlackScholesFX(spot, strike, time, r, rf, volatility) {
  let d1 =
    (Math.log(spot / strike) + (r - rf + 0.5 * volatility ** 2) * time) /
    (volatility * Math.sqrt(time));
  let d2 = d1 - volatility * Math.sqrt(time);
  return [d1, d2];
}

export function europeanBlackScholesFX(spot, strike, time, r, rf, volatility) {
  let d = d1d2BlackScholesFX(spot, strike, time, r, rf, volatility);
  let d1 = d[0],
    d2 = d[1];
  let call =
    spot * Math.exp(-rf * time) * cdfNormal(d1) -
    strike * Math.exp(-r * time) * cdfNormal(d2);
  let put =
    strike * Math.exp(-r * time) * cdfNormal(-d2) -
    spot * Math.exp(-rf * time) * cdfNormal(-d1);

  return [call, put];
}

/**
 * Simple linear regression
 *
 * @param {Array.<number>} data
 * @return {Function}
 */
export function regression(
  data,
  fieldX,
  fieldY,
  resField,
  slopeField,
  interceptField
) {
  let sum_x = 0,
    sum_y = 0,
    sum_xy = 0,
    sum_xx = 0,
    count = 0,
    m,
    b;

  if (data.length === 0) {
    throw new Error("Empty data");
  }

  // calculate sums
  let i = 0,
    len = data.length;
  for (; i < len; i++) {
    const point = data[i];
    sum_x += point[fieldX];
    sum_y += point[fieldY];
    sum_xx += point[fieldX] * point[fieldX];
    sum_xy += point[fieldX] * point[fieldY];
    count++;
  }

  // calculate slope (m) and y-intercept (b) for f(x) = m * x + b
  m = (count * sum_xy - sum_x * sum_y) / (count * sum_xx - sum_x * sum_x);
  b = sum_y / count - (m * sum_x) / count;

  data.forEach(function(d) {
    d[resField] = b + m * d[fieldX];
    d[slopeField] = m;
    d[interceptField] = b;
  });

  // return function(m, b, x) {
  //   return m * x + b;
  // }.bind(null, m, b);
  return data;
}

export function correlation(x, y) {
  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumX2 = 0,
    sumY2 = 0;
  const minLength = (x.length = y.length = Math.min(x.length, y.length)),
    reduce = (xi, idx) => {
      const yi = y[idx];
      sumX += xi;
      sumY += yi;
      sumXY += xi * yi;
      sumX2 += xi * xi;
      sumY2 += yi * yi;
    };
  x.forEach(reduce);
  return (
    (minLength * sumXY - sumX * sumY) /
    Math.sqrt(
      (minLength * sumX2 - sumX * sumX) * (minLength * sumY2 - sumY * sumY)
    )
  );
}

export function rollingCorrelation(data, fieldX, fieldY, n = 20) {
  let corr;
  let x = data.map(d => d[fieldX]);
  let y = data.map(d => d[fieldY]);
  data.forEach(function(d, i) {
    if (i < n) {
      corr = null;
    } else {
      corr = correlation(x.slice(i - n, i + 1), y.slice(i - n, i + 1));
    }
    d.corr = corr;
  });
  return data;
}
